<template>
  <v-card
    class="signature-mode-summary"
    data-test-id="signature-mode-summary"
    flat
    variant="flat"
  >
    <div class="signature-mode-summary__header">
      <div class="text-grey-darken-4 font-weight-bold">
        {{ i18n.t('titles.signature_mode_summary_card') }}
      </div>

      <div data-test-id="edit-observers">
        <v-btn
          class="text-none flex-shrink-0"
          color="primary"
          data-test-id="edit-signature-mode"
          density="comfortable"
          variant="text"
          @click="workflow.openFromCard(1)"
        >
          {{ i18n.t('buttons.edit') }}
        </v-btn>
      </div>
    </div>

    <div class="d-flex justify-space-between align-center ga-2">
      <div class="signature-mode-summary__content">
        <div class="text-grey-darken-2">
          {{ title }}
        </div>
        <div v-if="showJurisdiction" class="d-flex align-center ga-2 mt-2">
          <v-img
            :alt="jurisdictionLabel"
            class="flex-0-0 rounded-sm"
            cover
            height="16"
            :src="`/svg/flags/${flag}.svg`"
            width="24"
          />
          <span class="text-body-2 text-grey-darken-2">
            {{ jurisdictionLabel }}
          </span>
        </div>
        <div
          v-if="authorityService"
          class="d-flex align-center ga-2 mt-2"
          data-test-id="enforced-authority-service"
        >
          <span class="text-body-2 text-grey-darken-2">
            {{ i18n.t('labels.id_service') }}:
          </span>
          <v-img
            v-if="authorityServiceLogo"
            :alt="authorityServiceLabel"
            class="flex-0-0"
            height="16"
            position="left"
            :src="`/svg/authority-services/${authorityServiceLogo}-logo.svg`"
            width="64"
          />
          <span v-else class="text-body-2 text-grey-darken-2">
            {{ authorityServiceLabel }}
          </span>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCreateStore } from '@/stores/create';
import { useSigningWorkflow } from '@/composables/create/useSigningWorkflow';
import {
  getJurisdictionText,
  getSignatureLevelAcronym
} from '@/composables/useUtils';
import { SignatureMode } from '@/types/enums/SignatureMode';
import { Jurisdiction } from '@/types/enums/Jurisdiction';
import { AuthorityService } from '@/types/enums/AuthorityService';

const i18n = useI18n();
const createStore = useCreateStore();
const workflow = useSigningWorkflow();

const signatureMode = computed(() => createStore.document?.signatureMode);
const jurisdiction = computed(() => createStore.document?.jurisdiction);
// A non-null required authority service is what "enforced" means on the
// document; the draft only mirrors it while the dialog is open.
const authorityService = computed(
  () => createStore.document?.requiredAuthorityService
);

const modeNameKey: Record<SignatureMode, string> = {
  [SignatureMode.TIMESTAMP]: 'titles.basic_signature',
  [SignatureMode.ADVANCED]: 'titles.advanced_signature',
  [SignatureMode.QUALIFIED]: 'titles.qualified_signature'
};

const flagByJurisdiction: Partial<Record<Jurisdiction, string>> = {
  [Jurisdiction.ZERTES]: 'ch',
  [Jurisdiction.EIDAS]: 'eu'
};

const logoByAuthorityService: Partial<Record<AuthorityService, string>> = {
  [AuthorityService.DEEP_ID]: 'did',
  [AuthorityService.MOBILE_ID]: 'ras',
  [AuthorityService.SIGN8]: 'sign8'
};

const nameKeyByAuthorityService: Record<AuthorityService, string> = {
  [AuthorityService.DEEP_ID]: 'authorities.did',
  [AuthorityService.MOBILE_ID]: 'authorities.ras',
  [AuthorityService.YAPEAL_ID]: 'authorities.yapid',
  [AuthorityService.SIGN8]: 'authorities.sign8_description'
};

const title = computed(() => {
  const mode = signatureMode.value;
  if (!mode) return '';
  return `${getSignatureLevelAcronym(mode)} · ${i18n.t(modeNameKey[mode])}`;
});

const flag = computed(() =>
  jurisdiction.value ? flagByJurisdiction[jurisdiction.value] : undefined
);

const jurisdictionLabel = computed(() => {
  if (!jurisdiction.value) return '';
  const key = getJurisdictionText(jurisdiction.value);
  return key ? i18n.t(key) : '';
});

const showJurisdiction = computed(
  () =>
    signatureMode.value !== SignatureMode.TIMESTAMP &&
    !!flag.value &&
    !!jurisdictionLabel.value
);

// Mobile ID authenticates through an SMS code on advanced signatures, so its
// logo is replaced by the plain "SMS-Code" label there — same rule as the
// authority service menu and item.
const authorityServiceLogo = computed(() => {
  if (!authorityService.value) return undefined;
  if (
    signatureMode.value === SignatureMode.ADVANCED &&
    authorityService.value !== AuthorityService.DEEP_ID
  ) {
    return undefined;
  }
  return logoByAuthorityService[authorityService.value];
});

const authorityServiceLabel = computed(() => {
  const service = authorityService.value;
  if (!service) return '';
  if (!authorityServiceLogo.value && service === AuthorityService.MOBILE_ID) {
    return i18n.t('authorities.sms_code');
  }
  return i18n.t(nameKeyByAuthorityService[service]);
});
</script>

<style scoped lang="scss">
.signature-mode-summary {
  padding-left: 0;
  display: flex;
  flex-direction: column;
  row-gap: 2px;
}

.signature-mode-summary__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 34px;
}

.signature-mode-summary__content {
  min-width: 0;
}
</style>
